import React, { Fragment } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Table, Spinner, Badge } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetReturnSupplierByIdQuery, useDispatchReturnMutation } from '../../../../store/services/inventoryApi';
import { toast } from 'react-toastify';
import { ArrowLeft, Printer, Truck, Edit } from 'react-feather';

const ReturnToSupplierDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: returnResponse, isLoading, refetch } = useGetReturnSupplierByIdQuery(id);
    const [dispatchReturn, { isLoading: isDispatching }] = useDispatchReturnMutation();

    const returnData = returnResponse?.data || returnResponse;

    const handleDispatch = async () => {
        if (window.confirm("Are you sure you want to DISPATCH this return? This will immediately reduce stock in the store.")) {
            try {
                await dispatchReturn(id).unwrap();
                toast.success("Return Dispatched Successfully");
                refetch();
            } catch (err) {
                toast.error(err?.data?.message || "Failed to dispatch return");
            }
        }
    };

    if (isLoading) return <div className="text-center p-5"><Spinner color="primary" /></div>;
    if (!returnData) return <div className="text-center p-5">Return record not found</div>;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Return Details" parent="Inventory" title={`RTS-${returnData.returnNumber || id.slice(-6)}`} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5>
                                        Return #{returnData.returnNumber}
                                        <Badge className="ms-2" color={returnData.status === 'Draft' ? 'secondary' : 'success'}>
                                            {returnData.status}
                                        </Badge>
                                    </h5>
                                    <small className="text-muted">Created on {new Date(returnData.createdAt).toLocaleDateString()}</small>
                                </div>
                                <div className="d-flex gap-2 d-print-none">
                                    <Button color="light" onClick={() => navigate('/inventory/returns-to-supplier')}>
                                        <ArrowLeft size={14} className="me-1" /> Back
                                    </Button>
                                    <Button color="secondary" onClick={() => window.print()}>
                                        <Printer size={14} className="me-1" /> Print
                                    </Button>
                                    {returnData.status === 'Draft' && (
                                        <>
                                            <Button color="warning" onClick={() => navigate(`/inventory/returns-to-supplier/edit/${id}`)}>
                                                <Edit size={14} className="me-1" /> Edit
                                            </Button>
                                            <Button color="primary" onClick={handleDispatch} disabled={isDispatching}>
                                                {isDispatching ? <Spinner size="sm" /> : <><Truck size={14} className="me-1" /> Dispatch Return</>}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-4 text-center">
                                    <Col md="6">
                                        <Card className="bg-light h-100">
                                            <CardBody>
                                                <h6 className="text-primary border-bottom pb-2 mb-3">Source Store</h6>
                                                <h5 className="mb-1">{returnData.storeId?.storeName}</h5>
                                                <p className="mb-0 text-muted">Code: {returnData.storeId?.storeNumber}</p>
                                                <p className="mb-0 text-muted">{returnData.storeId?.storeAddress}</p>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md="6">
                                        <Card className="bg-light h-100">
                                            <CardBody>
                                                <h6 className="text-primary border-bottom pb-2 mb-3">Target Supplier</h6>
                                                <h5 className="mb-1">{returnData.supplierId?.name}</h5>
                                                <p className="mb-0 text-muted">{returnData.supplierId?.contactEmail}</p>
                                                <p className="mb-0 text-muted">{returnData.supplierId?.contactPhone}</p>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                <div className="mb-4">
                                    <h6 className="border-bottom pb-2 mb-3">Additional Information</h6>
                                    <p><strong>Notes:</strong> {returnData.notes || 'No notes provided'}</p>
                                    {returnData.dispatchedDate && (
                                        <p><strong>Dispatched On:</strong> {new Date(returnData.dispatchedDate).toLocaleString()}</p>
                                    )}
                                </div>

                                <Table hover responsive bordered className="mt-3">
                                    <thead className="table-light text-center">
                                        <tr>
                                            <th>#</th>
                                            <th className="text-start">Product Variant</th>
                                            <th>Quantity</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-center">
                                        {returnData.items?.map((item, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td className="text-start">{item.productVariantId}</td>
                                                <td className="fw-bold">{item.quantity}</td>
                                                <td>
                                                    <Badge color="light" className="text-dark">{item.reason}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default ReturnToSupplierDetails;
